"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.currencies = exports.maxMoney = exports.minMoney = exports.averageMoney = exports.sumMoney = exports.Money = void 0;
const tslib_1 = require("tslib");
const currencies = tslib_1.__importStar(require("@dinero.js/currencies"));
exports.currencies = currencies;
const dinero_js_1 = tslib_1.__importDefault(require("dinero.js"));
const formatNumber_1 = require("../formatNumber");
/**
 * Money value object class for handling monetary values with currency support
 *
 * Provides immutable money operations with proper currency handling and precision.
 * All operations return new Money instances to maintain immutability.
 * @example
 * ```typescript
 * const price = Money.fromAmount(19.99, 'USD');
 * const total = price.add(Money.fromAmount(5.00, 'USD'));
 * console.log(total.format()); // '$24.99'
 * ```
 */
class Money {
    /**
     * Creates a new Money instance
     * @param amount - The monetary amount as a decimal number
     * @param currency - The currency code (defaults to 'USD')
     * @throws {Error} When the currency is not supported
     * @example
     * ```typescript
     * const money = new Money(100.50, 'USD');
     * console.log(money.amount); // 100.5
     * ```
     */
    constructor(amount, currency = 'USD') {
        if (!currencies[currency]) {
            throw new Error(`Invalid currency: ${currency}`);
        }
        this._currency = currency;
        this._dinero = (0, dinero_js_1.default)({
            amount: Math.round(Math.pow(10, currencies[currency].exponent) * amount),
            currency,
            precision: currencies[currency].exponent,
        });
    }
    /**
     * Creates a Money instance from a decimal amount
     * @param amount - The monetary amount as a decimal number
     * @param currency - The currency code (defaults to 'USD')
     * @returns New Money instance
     * @throws {Error} When the currency is not supported
     * @example
     * ```typescript
     * const price = Money.fromAmount(19.99, 'USD');
     * console.log(price.amount); // 19.99
     * console.log(price.currency); // 'USD'
     * ```
     */
    static fromAmount(amount, currency = 'USD') {
        return new Money(amount, currency);
    }
    /**
     * Creates a Money instance from cents (smallest currency unit)
     * @param cents - The amount in cents (or smallest currency unit)
     * @param currency - The currency code (defaults to 'USD')
     * @returns New Money instance
     * @throws {Error} When the currency is not supported
     * @example
     * ```typescript
     * const price = Money.fromCents(1999, 'USD');
     * console.log(price.amount); // 19.99
     * console.log(price.cents); // 1999
     * ```
     */
    static fromCents(cents, currency = 'USD') {
        // Validate currency first to prevent TypeError
        if (!currencies[currency]) {
            throw new Error(`Invalid currency: ${currency}`);
        }
        const dineroInstance = (0, dinero_js_1.default)({
            amount: cents,
            currency,
            precision: currencies[currency].exponent,
        });
        const money = Object.create(Money.prototype);
        money._currency = currency;
        money._dinero = dineroInstance;
        return money;
    }
    /**
     * Creates a Money instance from a string amount
     * @param amount - The monetary amount as a string
     * @param currency - The currency code (defaults to 'USD')
     * @returns New Money instance, or zero amount if string is invalid
     * @throws {Error} When the currency is not supported
     * @example
     * ```typescript
     * const price = Money.fromString('19.99', 'USD');
     * console.log(price.amount); // 19.99
     *
     * const invalid = Money.fromString('invalid', 'USD');
     * console.log(invalid.amount); // 0
     * ```
     */
    static fromString(amount, currency = 'USD') {
        const amountNumber = Number.parseFloat(amount);
        if (Number.isNaN(amountNumber)) {
            return Money.zero(currency);
        }
        return new Money(amountNumber, currency);
    }
    /**
     * Creates a Money instance with zero amount
     * @param currency - The currency code (defaults to 'USD')
     * @returns New Money instance with zero amount
     * @throws {Error} When the currency is not supported
     * @example
     * ```typescript
     * const zero = Money.zero('EUR');
     * console.log(zero.amount); // 0
     * console.log(zero.isZero()); // true
     * ```
     */
    static zero(currency = 'USD') {
        return new Money(0, currency);
    }
    // Getters
    /**
     * Gets the monetary amount as a decimal number
     * @returns The amount as a decimal number
     * @example
     * ```typescript
     * const money = Money.fromAmount(19.99, 'USD');
     * console.log(money.amount); // 19.99
     * ```
     */
    get amount() {
        return this._dinero.toUnit();
    }
    /**
     * Gets the monetary amount in cents (smallest currency unit)
     * @returns The amount in cents
     * @example
     * ```typescript
     * const money = Money.fromAmount(19.99, 'USD');
     * console.log(money.cents); // 1999
     * ```
     */
    get cents() {
        return this._dinero.getAmount();
    }
    /**
     * Gets the currency code
     * @returns The currency code
     * @example
     * ```typescript
     * const money = Money.fromAmount(19.99, 'USD');
     * console.log(money.currency); // 'USD'
     * ```
     */
    get currency() {
        return this._currency;
    }
    /**
     * Gets the underlying Dinero instance
     * @returns The Dinero instance
     */
    get dinero() {
        return this._dinero;
    }
    /**
     * Adds another Money amount to this Money
     * @param other - The Money amount to add
     * @returns New Money instance with the sum
     * @throws {Error} When currencies don't match
     * @example
     * ```typescript
     * const price1 = Money.fromAmount(10.50, 'USD');
     * const price2 = Money.fromAmount(5.25, 'USD');
     * const total = price1.add(price2);
     * console.log(total.amount); // 15.75
     * ```
     */
    add(other) {
        if (this.currency !== other.currency) {
            throw new Error(`Cannot add money with different currencies: ${this.currency} and ${other.currency}`);
        }
        const result = this._dinero.add(other._dinero);
        const money = new Money(0, this.currency);
        money._dinero = result;
        return money;
    }
    /**
     * Subtracts another Money amount from this Money
     * @param other - The Money amount to subtract
     * @returns New Money instance with the difference
     * @throws {Error} When currencies don't match
     * @example
     * ```typescript
     * const price1 = Money.fromAmount(10.50, 'USD');
     * const price2 = Money.fromAmount(3.25, 'USD');
     * const difference = price1.subtract(price2);
     * console.log(difference.amount); // 7.25
     * ```
     */
    subtract(other) {
        if (this.currency !== other.currency) {
            throw new Error(`Cannot subtract money with different currencies: ${this.currency} and ${other.currency}`);
        }
        const result = this._dinero.subtract(other._dinero);
        const money = new Money(0, this.currency);
        money._dinero = result;
        return money;
    }
    /**
     * Multiplies this Money by a number
     * @param multiplier - The number to multiply by
     * @returns New Money instance with the product
     * @example
     * ```typescript
     * const price = Money.fromAmount(10.00, 'USD');
     * const doubled = price.multiply(2);
     * console.log(doubled.amount); // 20.00
     * ```
     */
    multiply(multiplier) {
        const result = this._dinero.multiply(multiplier);
        const money = new Money(0, this.currency);
        money._dinero = result;
        return money;
    }
    /**
     * Divides this Money by a number
     * @param divisor - The number to divide by
     * @returns New Money instance with the quotient
     * @throws {Error} When dividing by zero
     * @example
     * ```typescript
     * const price = Money.fromAmount(20.00, 'USD');
     * const half = price.divide(2);
     * console.log(half.amount); // 10.00
     * ```
     */
    divide(divisor) {
        if (divisor === 0) {
            throw new Error('Cannot divide by zero');
        }
        const result = this._dinero.divide(divisor);
        const money = new Money(0, this.currency);
        money._dinero = result;
        return money;
    }
    /**
     * Checks if this Money equals another Money
     * @param other - The Money to compare with
     * @returns True if amounts and currencies are equal, false otherwise
     * @example
     * ```typescript
     * const price1 = Money.fromAmount(10.00, 'USD');
     * const price2 = Money.fromAmount(10.00, 'USD');
     * console.log(price1.equals(price2)); // true
     * ```
     */
    equals(other) {
        if (this.currency !== other.currency) {
            return false;
        }
        return this._dinero.equalsTo(other._dinero);
    }
    /**
     * Checks if this Money is greater than another Money
     * @param other - The Money to compare with
     * @returns True if this amount is greater than the other
     * @throws {Error} When currencies don't match
     * @example
     * ```typescript
     * const price1 = Money.fromAmount(15.00, 'USD');
     * const price2 = Money.fromAmount(10.00, 'USD');
     * console.log(price1.greaterThan(price2)); // true
     * ```
     */
    greaterThan(other) {
        if (this.currency !== other.currency) {
            throw new Error(`Cannot compare money with different currencies: ${this.currency} and ${other.currency}`);
        }
        return this._dinero.greaterThan(other._dinero);
    }
    /**
     * Checks if this Money is greater than or equal to another Money
     * @param other - The Money to compare with
     * @returns True if this amount is greater than or equal to the other
     * @throws {Error} When currencies don't match
     * @example
     * ```typescript
     * const price1 = Money.fromAmount(10.00, 'USD');
     * const price2 = Money.fromAmount(10.00, 'USD');
     * console.log(price1.greaterThanOrEqual(price2)); // true
     * ```
     */
    greaterThanOrEqual(other) {
        if (this.currency !== other.currency) {
            throw new Error(`Cannot compare money with different currencies: ${this.currency} and ${other.currency}`);
        }
        return this._dinero.greaterThanOrEqual(other._dinero);
    }
    /**
     * Checks if this Money is less than another Money
     * @param other - The Money to compare with
     * @returns True if this amount is less than the other
     * @throws {Error} When currencies don't match
     * @example
     * ```typescript
     * const price1 = Money.fromAmount(5.00, 'USD');
     * const price2 = Money.fromAmount(10.00, 'USD');
     * console.log(price1.lessThan(price2)); // true
     * ```
     */
    lessThan(other) {
        if (this.currency !== other.currency) {
            throw new Error(`Cannot compare money with different currencies: ${this.currency} and ${other.currency}`);
        }
        return this._dinero.lessThan(other._dinero);
    }
    /**
     * Checks if this Money is less than or equal to another Money
     * @param other - The Money to compare with
     * @returns True if this amount is less than or equal to the other
     * @throws {Error} When currencies don't match
     * @example
     * ```typescript
     * const price1 = Money.fromAmount(10.00, 'USD');
     * const price2 = Money.fromAmount(10.00, 'USD');
     * console.log(price1.lessThanOrEqual(price2)); // true
     * ```
     */
    lessThanOrEqual(other) {
        if (this.currency !== other.currency) {
            throw new Error(`Cannot compare money with different currencies: ${this.currency} and ${other.currency}`);
        }
        return this._dinero.lessThanOrEqual(other._dinero);
    }
    /**
     * Checks if this Money amount is zero
     * @returns True if the amount is zero
     * @example
     * ```typescript
     * const zero = Money.zero('USD');
     * console.log(zero.isZero()); // true
     * ```
     */
    isZero() {
        return this._dinero.isZero();
    }
    /**
     * Checks if this Money amount is positive
     * @returns True if the amount is positive
     * @example
     * ```typescript
     * const positive = Money.fromAmount(10.00, 'USD');
     * console.log(positive.isPositive()); // true
     * ```
     */
    isPositive() {
        return this._dinero.isPositive();
    }
    /**
     * Checks if this Money amount is negative
     * @returns True if the amount is negative
     * @example
     * ```typescript
     * const negative = Money.fromAmount(-10.00, 'USD');
     * console.log(negative.isNegative()); // true
     * ```
     */
    isNegative() {
        return this._dinero.isNegative();
    }
    // Utility methods
    /**
     * Returns the absolute value of this Money
     * @returns New Money instance with the absolute amount
     * @example
     * ```typescript
     * const negative = Money.fromAmount(-10.00, 'USD');
     * const absolute = negative.abs();
     * console.log(absolute.amount); // 10.00
     * ```
     */
    abs() {
        // For absolute value, we can create a new Money instance with the absolute amount
        const absoluteAmount = Math.abs(this.amount);
        return new Money(absoluteAmount, this.currency);
    }
    /**
     * Formats this Money as a currency string with customizable display options
     * @param opts - Formatting options for currency display
     * @param opts.currencyDisplay - How to display the currency ('code', 'symbol', 'narrowSymbol', 'name')
     * @param opts.locale - Locale for formatting (defaults to browser locale or 'en-US')
     * @param opts.maximumFractionDigits - Maximum number of decimal places
     * @param opts.minimumFractionDigits - Minimum number of decimal places
     * @param opts.style - Number format style ('currency', 'decimal', 'percent')
     * @returns Formatted string with currency symbol
     * @example
     * ```typescript
     * const price = Money.fromAmount(19.99, 'USD');
     *
     * // Default formatting
     * console.log(price.format()); // '$19.99'
     *
     * // Custom currency display
     * console.log(price.format({ currencyDisplay: 'code' })); // 'USD 19.99'
     * console.log(price.format({ currencyDisplay: 'name' })); // '19.99 US dollars'
     *
     * // Custom locale
     * console.log(price.format({ locale: 'fr-FR' })); // '19,99 $US'
     *
     * // Custom decimal places
     * console.log(price.format({ maximumFractionDigits: 0 })); // '$20'
     * console.log(price.format({ minimumFractionDigits: 3 })); // '$19.990'
     *
     * // EUR example
     * const euroPrice = Money.fromAmount(25.50, 'EUR');
     * console.log(euroPrice.format({ locale: 'de-DE' })); // '25,50 €'
     * ```
     */
    format(opts = {}) {
        const options = Object.assign({ currencyDisplay: 'narrowSymbol' }, opts);
        return (0, formatNumber_1.numberWithCurrency)({
            value: this.amount,
            currency: {
                code: this.currency,
            },
            options,
        });
    }
    /**
     * Converts this Money to a JSON-serializable object
     * @returns MoneyValue object with amount, currency, and dinero instance
     * @example
     * ```typescript
     * const price = Money.fromAmount(19.99, 'USD');
     * const json = price.toJSON();
     * console.log(json.amount); // 19.99
     * console.log(json.currency); // 'USD'
     * ```
     */
    toJSON() {
        return {
            amount: this.amount,
            currency: this.currency,
            dinero: this._dinero,
        };
    }
    /**
     * Converts this Money to a string representation
     * @returns Simple string format with amount and currency code
     * @example
     * ```typescript
     * const price = Money.fromAmount(19.99, 'USD');
     * console.log(price.toString()); // '19.99 USD'
     * ```
     */
    toString() {
        return `${this.amount} ${this.currency}`;
    }
    /**
     * Converts this Money to another currency using an exchange rate
     * @param targetCurrency - The target currency code
     * @param exchangeRate - The exchange rate to apply
     * @returns New Money instance in the target currency
     * @throws {Error} When the target currency is not supported
     * @example
     * ```typescript
     * const usdPrice = Money.fromAmount(100, 'USD');
     * const eurPrice = usdPrice.convertTo('EUR', 0.85);
     * console.log(eurPrice.amount); // 85
     * console.log(eurPrice.currency); // 'EUR'
     * ```
     */
    convertTo(targetCurrency, exchangeRate) {
        if (this.currency === targetCurrency) {
            return this;
        }
        const convertedAmount = this.amount * exchangeRate;
        return new Money(convertedAmount, targetCurrency);
    }
}
exports.Money = Money;
/**
 * Sums an array of Money values
 * @param moneyArray - Array of Money values to sum
 * @returns New Money instance with the total sum
 * @throws {Error} When array is empty or currencies don't match
 * @example
 * ```typescript
 * const prices = [
 *   Money.fromAmount(10.50, 'USD'),
 *   Money.fromAmount(5.25, 'USD'),
 *   Money.fromAmount(3.75, 'USD')
 * ];
 * const total = sumMoney(prices);
 * console.log(total.amount); // 19.50
 * ```
 */
const sumMoney = (moneyArray) => {
    if (moneyArray.length === 0) {
        throw new Error('Cannot sum empty array of money');
    }
    const firstCurrency = moneyArray[0].currency;
    const allSameCurrency = moneyArray.every((money) => money.currency === firstCurrency);
    if (!allSameCurrency) {
        throw new Error('Cannot sum money with different currencies');
    }
    // eslint-disable-next-line unicorn/no-array-reduce
    return moneyArray.reduce((sum, money) => sum.add(money), Money.zero(firstCurrency));
};
exports.sumMoney = sumMoney;
/**
 * Calculates the average of an array of Money values
 * @param moneyArray - Array of Money values to average
 * @returns New Money instance with the average amount
 * @throws {Error} When array is empty
 * @example
 * ```typescript
 * const prices = [
 *   Money.fromAmount(10.00, 'USD'),
 *   Money.fromAmount(20.00, 'USD'),
 *   Money.fromAmount(30.00, 'USD')
 * ];
 * const average = averageMoney(prices);
 * console.log(average.amount); // 20.00
 * ```
 */
const averageMoney = (moneyArray) => {
    if (moneyArray.length === 0) {
        throw new Error('Cannot calculate average of empty array');
    }
    const sum = (0, exports.sumMoney)(moneyArray);
    return sum.divide(moneyArray.length);
};
exports.averageMoney = averageMoney;
/**
 * Finds the minimum Money value in an array
 * @param moneyArray - Array of Money values to compare
 * @returns The Money instance with the smallest amount
 * @throws {Error} When array is empty
 * @example
 * ```typescript
 * const prices = [
 *   Money.fromAmount(30.00, 'USD'),
 *   Money.fromAmount(10.00, 'USD'),
 *   Money.fromAmount(20.00, 'USD')
 * ];
 * const min = minMoney(prices);
 * console.log(min.amount); // 10.00
 * ```
 */
const minMoney = (moneyArray) => {
    if (moneyArray.length === 0) {
        throw new Error('Cannot find minimum of empty array');
    }
    // eslint-disable-next-line unicorn/no-array-reduce
    return moneyArray.reduce((min, money) => (money.lessThan(min) ? money : min));
};
exports.minMoney = minMoney;
/**
 * Finds the maximum Money value in an array
 * @param moneyArray - Array of Money values to compare
 * @returns The Money instance with the largest amount
 * @throws {Error} When array is empty
 * @example
 * ```typescript
 * const prices = [
 *   Money.fromAmount(10.00, 'USD'),
 *   Money.fromAmount(30.00, 'USD'),
 *   Money.fromAmount(20.00, 'USD')
 * ];
 * const max = maxMoney(prices);
 * console.log(max.amount); // 30.00
 * ```
 */
const maxMoney = (moneyArray) => {
    if (moneyArray.length === 0) {
        throw new Error('Cannot find maximum of empty array');
    }
    // eslint-disable-next-line unicorn/no-array-reduce
    return moneyArray.reduce((max, money) => money.greaterThan(max) ? money : max);
};
exports.maxMoney = maxMoney;
//# sourceMappingURL=index.js.map